import privacyPolicyKk from "./legal/privacy-policy/kk.md?raw";
import privacyPolicyRu from "./legal/privacy-policy/ru.md?raw";
import privacyPolicyEn from "./legal/privacy-policy/en.md?raw";
import userAgreementKk from "./legal/user-agreement/kk.md?raw";
import userAgreementRu from "./legal/user-agreement/ru.md?raw";
import userAgreementEn from "./legal/user-agreement/en.md?raw";
import safetyServiceRulesKk from "./legal/safety-service-rules/kk.md?raw";
import safetyServiceRulesRu from "./legal/safety-service-rules/ru.md?raw";
import safetyServiceRulesEn from "./legal/safety-service-rules/en.md?raw";
import personalDataConsentKk from "./legal/personal-data-consent/kk.md?raw";
import personalDataConsentRu from "./legal/personal-data-consent/ru.md?raw";
import personalDataConsentEn from "./legal/personal-data-consent/en.md?raw";
import emergencyDataTransferConsentKk from "./legal/emergency-data-transfer-consent/kk.md?raw";
import emergencyDataTransferConsentRu from "./legal/emergency-data-transfer-consent/ru.md?raw";
import emergencyDataTransferConsentEn from "./legal/emergency-data-transfer-consent/en.md?raw";
import videoGreetingConsentKk from "./legal/video-greeting-publication-consent/kk.md?raw";
import videoGreetingConsentRu from "./legal/video-greeting-publication-consent/ru.md?raw";
import videoGreetingConsentEn from "./legal/video-greeting-publication-consent/en.md?raw";

export const LEGAL_DOCUMENT_LOCALES = ["kk", "ru", "en"] as const;
export type LegalDocumentLocale = (typeof LEGAL_DOCUMENT_LOCALES)[number];

export type LegalDocumentId =
  | "privacy-policy"
  | "user-agreement"
  | "safety-service-rules"
  | "personal-data-consent"
  | "emergency-data-transfer-consent"
  | "video-greeting-consent";

export interface LegalDocument {
  id: LegalDocumentId;
  version: string;
  translations: Record<LegalDocumentLocale, string>;
}

const versionFromMarkdown = (markdown: string): string => {
  const revisionLine = markdown.match(/^(?:_|\*)?(?:Редакция|Revision|Version|Редакциясы)\s*:\s*([\d.]+)/im);
  return revisionLine?.[1] ?? "1.0";
};

const defineLegalDocument = (
  id: LegalDocumentId,
  translations: Record<LegalDocumentLocale, string>,
): LegalDocument => ({
  id,
  // Consent APIs persist this value. Kazakh is the legal viewer fallback, so
  // its copy is the canonical source if locale files ever drift.
  version: versionFromMarkdown(translations.kk),
  translations,
});

export const privacyPolicy = defineLegalDocument("privacy-policy", {
  kk: privacyPolicyKk,
  ru: privacyPolicyRu,
  en: privacyPolicyEn,
});

export const userAgreement = defineLegalDocument("user-agreement", {
  kk: userAgreementKk,
  ru: userAgreementRu,
  en: userAgreementEn,
});

// Compatibility name used by onboarding and its persisted version field.
export const termsOfService = userAgreement;

export const safetyServiceRules = defineLegalDocument("safety-service-rules", {
  kk: safetyServiceRulesKk,
  ru: safetyServiceRulesRu,
  en: safetyServiceRulesEn,
});

export const serviceRules = safetyServiceRules;

export const personalDataConsent = defineLegalDocument("personal-data-consent", {
  kk: personalDataConsentKk,
  ru: personalDataConsentRu,
  en: personalDataConsentEn,
});

export const dataConsent = personalDataConsent;

export const emergencyDataTransferConsent = defineLegalDocument("emergency-data-transfer-consent", {
  kk: emergencyDataTransferConsentKk,
  ru: emergencyDataTransferConsentRu,
  en: emergencyDataTransferConsentEn,
});

export const dchsDataConsent = emergencyDataTransferConsent;

export const videoGreetingConsent = defineLegalDocument("video-greeting-consent", {
  kk: videoGreetingConsentKk,
  ru: videoGreetingConsentRu,
  en: videoGreetingConsentEn,
});

export const legalDocuments: readonly LegalDocument[] = [
  privacyPolicy,
  userAgreement,
  safetyServiceRules,
  personalDataConsent,
  emergencyDataTransferConsent,
  videoGreetingConsent,
];

export const resolveLegalDocumentLocale = (
  document: LegalDocument,
  locale: string,
): LegalDocumentLocale => {
  const normalized = locale.toLowerCase().split("-")[0];
  if (normalized === "kz") return "kk";
  if (LEGAL_DOCUMENT_LOCALES.includes(normalized as LegalDocumentLocale)) {
    const supported = normalized as LegalDocumentLocale;
    if (document.translations[supported]) return supported;
  }
  return document.translations.kk
    ? "kk"
    : LEGAL_DOCUMENT_LOCALES.find((candidate) => document.translations[candidate]) ?? "kk";
};

export const preferredLegalDocumentLocale = (document: LegalDocument): LegalDocumentLocale => {
  if (typeof window === "undefined") return "kk";

  const stored = window.localStorage.getItem("ecoway_locale");
  if (stored) return resolveLegalDocumentLocale(document, stored);

  const browserLanguages = window.navigator.languages?.length
    ? window.navigator.languages
    : [window.navigator.language];
  for (const language of browserLanguages) {
    const resolved = resolveLegalDocumentLocale(document, language);
    const normalized = language.toLowerCase().split("-")[0];
    if (normalized === "kz" || normalized === resolved) return resolved;
  }
  return "kk";
};
